/**
 * Mirrors: api/internal/httpapi/runs.go
 *
 *   GET  /runs                 paged in SQL
 *   GET  /runs/:id
 *   GET  /runs/:id/stream      SSE: `event: run` frames, then a terminal `event: done`
 *   POST /runs                 202 Accepted
 *   POST /runs/:id/cancel
 *
 * The three collector counters are INT NULL in the schema, so they arrive as
 * `number | null`. `progress_percent` is null with them: "0 of 0" is never
 * emitted for an unmeasured run, and the UI must not invent it either.
 */
import { API_BASE, get, post } from "@/lib/api";
import { withQuery } from "./params";
import type { Page, PageParams } from "./types";
import { tenantPath, tenantScopeKey } from "./tenantScope";

export type RunStatus = string;

export interface Run {
  id: string;
  trigger: string;
  status: RunStatus;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  collectors_total: number | null;
  collectors_done: number | null;
  collectors_failed: number | null;
  progress_percent: number | null;
  active: boolean;
  error_message?: string;
  capability_snapshot?: unknown;
}

/**
 * Go omits a nil *int as null; a field the server omitted entirely would read
 * back as `undefined`, which is the same claim ("not known") spelled
 * differently. This collapses the two so components only test for null.
 */
export function normaliseRun(run: Run): Run {
  return {
    ...run,
    started_at: run.started_at ?? null,
    finished_at: run.finished_at ?? null,
    collectors_total: run.collectors_total ?? null,
    collectors_done: run.collectors_done ?? null,
    collectors_failed: run.collectors_failed ?? null,
    progress_percent: run.progress_percent ?? null,
  };
}

export const runKeys = {
  all: () => ["runs", tenantScopeKey()] as const,
  list: (params: PageParams) =>
    ["runs", tenantScopeKey(), "list", params.page, params.size] as const,
  detail: (id: string) => ["runs", tenantScopeKey(), "detail", id] as const,
};

export async function fetchRuns(params: PageParams): Promise<Page<Run>> {
  const raw = await get<Page<Run>>(withQuery(tenantPath("/runs"), { page: params.page, size: params.size }));
  return { ...raw, items: (raw.items ?? []).map(normaliseRun) };
}

export async function fetchRun(id: string): Promise<Run> {
  return normaliseRun(await get<Run>(tenantPath(`/runs/${encodeURIComponent(id)}`)));
}

export async function startRun(trigger?: string): Promise<Run> {
  return normaliseRun(await post<Run>(tenantPath("/runs"), trigger === undefined ? {} : { trigger }));
}

export async function cancelRun(id: string): Promise<Run> {
  return normaliseRun(await post<Run>(tenantPath(`/runs/${encodeURIComponent(id)}/cancel`)));
}

/** The SSE endpoint for one run. Same-origin, so EventSource sends the cookie. */
export function runStreamUrl(id: string): string {
  return `${API_BASE}${tenantPath(`/runs/${encodeURIComponent(id)}/stream`)}`;
}

/** The payload of the terminal `event: done` frame. */
export interface RunDoneEvent {
  run_id: string;
  status: string;
}
